(function () {
  const ESTIMATOR_DEBOUNCE_MS = 60;
  const ESTIMATOR_ANIM_MS = 260;

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function parseEstimatorWidth(value) {
    const parsed = parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function ceilToMajorUnits(rawCents) {
    if (!Number.isFinite(rawCents) || rawCents <= 0) return 0;
    return Math.ceil(rawCents / 100);
  }

  function runAnimation(fromValue, toValue, duration, onFrame, onDone) {
    let start = null;
    function frame(timestamp) {
      if (start === null) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);
      const current = Math.round(fromValue + (toValue - fromValue) * eased);
      onFrame(current);
      if (progress < 1) {
        requestAnimationFrame(frame);
      } else if (onDone) {
        onDone();
      }
    }
    requestAnimationFrame(frame);
  }

  class CurtainSizeSelector {
    constructor(root) {
      this.root = root;
      if (!this.root || this.root.dataset.initialized === 'true') return;
      this.root.dataset.initialized = 'true';

      this.config = this.getConfig();
      this.blockId = (this.config && this.config.blockId) || this.root.dataset.cssId || '';
      this.productHandle =
        (this.config && this.config.productHandle) || this.root.dataset.productHandle || '';
      this.hasHeadingVariants = !!(this.config && this.config.hasHeadingVariants);
      this.widthOptionIndex = Number(this.config && this.config.widthOptionIndex) || 0;
      this.headingOptionIndex = Number(this.config && this.config.headingOptionIndex) || 0;
      this.storefrontAccessToken = this.root.dataset.storefrontAccessToken || '';
      this.variantsMap = {};
      this.variantsData = {};
      this.productHeadingKeys = new Set();
      this.availableWidthsByHeading = {};
      this.variantsLoadPromise = null;
      this.variantsFetchSettled = false;

      this.modeInputs = Array.from(this.root.querySelectorAll('[data-css-mode-input]'));
      this.measurePanel = this.root.querySelector('[data-css-panel="measure"]');
      this.standardPanel = this.root.querySelector('[data-css-panel="standard"]');
      this.scrollBtn = this.root.querySelector('[data-css-scroll-btn]');
      this.headingSelect = this.root.querySelector('[data-css-heading-select]');
      this.sizeSelect = this.root.querySelector('[data-css-size-select]');
      this.widthSelect = this.root.querySelector('[data-css-width-select]');
      this.qtyInput = this.root.querySelector('[data-css-qty-input]');
      this.qtyDecrease = this.root.querySelector('[data-css-qty-decrease]');
      this.qtyIncrease = this.root.querySelector('[data-css-qty-increase]');
      this.totalPrice = this.root.querySelector('[data-css-total-price]');
      this.addBtn = this.root.querySelector('[data-css-add-btn]');
      this.status = this.root.querySelector('[data-css-status]');
      this.standardSummary = this.root.querySelector('[data-css-standard-summary]');
      this.defaultAddLabel = this.addBtn ? this.addBtn.textContent.trim() : 'Add to cart';
      this.estimatorWidthInput = this.root.querySelector('[data-css-estimator-width]');
      this.estimatorPrice = this.root.querySelector('[data-css-estimator-price]');
      this.estimatorTooltipBtn = this.root.querySelector('[data-css-estimator-tooltip-trigger]');
      this.estimatorTooltipPanel = this.root.querySelector('[data-css-estimator-tooltip-panel]');
      this.estimatorConfig = this.getEstimatorConfig();
      this.estimatorAnimToken = 0;
      this.estimatorDebounceTimer = null;
      this.estimatorDisplayedMajor = Number(this.estimatorPrice && this.estimatorPrice.dataset.cssEstimatorInitialMajor) || 0;
      this.estimatorLastValidWidth = parseEstimatorWidth(
        (this.estimatorWidthInput && this.estimatorWidthInput.value) ||
          (this.estimatorConfig && this.estimatorConfig.defaultWidth) ||
          0
      );
      if (!this.estimatorLastValidWidth) {
        this.estimatorLastValidWidth = parseEstimatorWidth(
          (this.estimatorConfig && this.estimatorConfig.defaultWidth) || 0.01
        ) || 0.01;
      }
      this.standardPriceAnimToken = 0;
      this.displayedStandardTotalCents = 0;

      this.isAdding = false;
      this.currencyFormatter = this.createCurrencyFormatter();
      this.currentVariant = null;
      this.bindEvents();
      this.setMode(this.config.modeDefault || 'measure');
      this.bootstrapVariants();
      this.updateEstimatorInstant();
      this.updateStandardSummary({ animate: false });
    }

    filterHeadingOptions() {
      if (!this.headingSelect || (this.config && this.config.showPocketHeadingOption)) return;

      Array.from(this.headingSelect.options).forEach((option) => {
        if (this.normalizeHeadingOptionValue(option.value) === 'pocket') {
          option.remove();
        }
      });
    }

    bootstrapVariants() {
      this.filterHeadingOptions();
      const inlineEntries = this.getInlineVariantsMappingEntries();
      if (inlineEntries.length) {
        this.buildVariantsMapFromEntries(inlineEntries);
      }
      this.syncWidthOptionsForHeading({ preserveSelection: true });
      this.refreshVariantAndPrice({ animatePrice: false });
      this.loadFullVariantsMapping().then(() => {
        this.syncWidthOptionsForHeading({ preserveSelection: true });
        this.refreshVariantAndPrice({ animatePrice: false });
      });
    }

    refreshVariantAndPrice(options = {}) {
      const { animatePrice = true } = options;
      this.currentVariant = this.resolveSelectedVariant();
      this.applyVariantState();
      if (animatePrice) {
        this.updatePrice();
      } else {
        this.updatePriceInstant();
      }
      this.updateStandardSummary({ animate: false });
    }

    positionEstimatorTooltipForMobile() {
      if (!this.estimatorTooltipPanel || !this.estimatorTooltipBtn) return;
      if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;

      // Reset any previous inline positioning so we measure the CSS-default centered state.
      this.estimatorTooltipPanel.style.left = '';
      this.estimatorTooltipPanel.style.right = '';

      const padding = 12;
      const panelRect = this.estimatorTooltipPanel.getBoundingClientRect();
      const viewportW = (window.visualViewport && window.visualViewport.width) || window.innerWidth || 0;
      if (!viewportW) return;

      // If the panel would overflow the viewport, pin it to the side closest to the trigger.
      if (panelRect.left < padding) {
        this.estimatorTooltipPanel.style.left = '0';
        this.estimatorTooltipPanel.style.right = 'auto';
      } else if (panelRect.right > viewportW - padding) {
        this.estimatorTooltipPanel.style.left = 'auto';
        this.estimatorTooltipPanel.style.right = '0';
      }
    }

    getConfig() {
      const raw = this.root.querySelector('[data-css-config]');
      if (!raw) return {};
      try {
        return JSON.parse(raw.textContent);
      } catch (error) {
        console.error('Curtain size selector config parse error:', error);
        return {};
      }
    }

    createCurrencyFormatter() {
      const locale = this.config.localeCode || 'en';
      const currency = this.config.currencyCode || 'USD';
      try {
        if (String(currency).toUpperCase() === 'ZAR') {
          const randFormatter = new Intl.NumberFormat('en-ZA', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          return {
            format(amountNumber) {
              return `R ${randFormatter.format(amountNumber)}`;
            }
          };
        }
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      } catch (error) {
        return new Intl.NumberFormat('en', {
          style: 'currency',
          currency: 'USD'
        });
      }
    }

    getEstimatorConfig() {
      const raw = this.root.querySelector('[data-css-estimator-config]');
      if (!raw) return null;
      try {
        return JSON.parse(raw.textContent);
      } catch (error) {
        console.error('Curtain estimator config parse error:', error);
        return null;
      }
    }

    createEstimatorFormatter() {
      const locale = (this.estimatorConfig && this.estimatorConfig.locale) || this.config.localeCode || 'en';
      const currency = (this.estimatorConfig && this.estimatorConfig.currencyCode) || this.config.currencyCode || 'USD';
      const useRandPrefix = !!(this.estimatorConfig && this.estimatorConfig.useRandPrefix);

      if (useRandPrefix) {
        const randFormatter = new Intl.NumberFormat('en-ZA', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
        return {
          format(majorUnits) {
            return `R ${randFormatter.format(majorUnits)}`;
          }
        };
      }

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      } catch (error) {
        return this.currencyFormatter;
      }
    }

    bindEvents() {
      this.modeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.setMode(input.value);
        });
      });

      if (this.scrollBtn) {
        this.scrollBtn.addEventListener('click', () => {
          this.blurActiveField();
          this.scrollToNextSection();
        });
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => {
          this.sanitizeQty();
          this.updatePrice();
          this.updateStandardSummary();
        });
        this.qtyInput.addEventListener('change', () => {
          this.sanitizeQty();
          this.updatePrice();
          this.updateStandardSummary();
        });
      }

      if (this.qtyDecrease) {
        this.qtyDecrease.addEventListener('click', () => {
          const next = this.getQty() - 1;
          this.setQty(next < 1 ? 1 : next);
          this.updateStandardSummary();
        });
      }

      if (this.qtyIncrease) {
        this.qtyIncrease.addEventListener('click', () => {
          const next = this.getQty() + 1;
          this.setQty(next);
          this.updateStandardSummary();
        });
      }

      if (this.addBtn) {
        this.addBtn.addEventListener('click', () => this.addToCart());
      }

      if (this.headingSelect) {
        this.headingSelect.addEventListener('change', () => {
          this.syncWidthOptionsForHeading({ preserveSelection: true });
          this.refreshVariantAndPrice();
        });
      }
      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', () => this.updateStandardSummary());
      }
      if (this.widthSelect) {
        this.widthSelect.addEventListener('change', () => this.refreshVariantAndPrice());
      }

      if (this.estimatorWidthInput) {
        this.estimatorWidthInput.addEventListener('input', () => this.scheduleEstimatorUpdate());
        this.estimatorWidthInput.addEventListener('change', () => {
          this.normalizeEstimatorInput();
          this.scheduleEstimatorUpdate();
        });
      }

      if (this.estimatorTooltipBtn && this.estimatorTooltipPanel) {
        this.estimatorTooltipBtn.addEventListener('click', (event) => {
          event.preventDefault();
          const willOpen = !this.estimatorTooltipPanel.classList.contains('is-open');
          this.estimatorTooltipPanel.classList.toggle('is-open', willOpen);
          this.estimatorTooltipBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          if (willOpen) {
            // Wait a frame so the panel has its open transform applied before measuring.
            requestAnimationFrame(() => this.positionEstimatorTooltipForMobile());
          }
        });

        this.estimatorTooltipBtn.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            this.estimatorTooltipPanel.classList.remove('is-open');
            this.estimatorTooltipBtn.setAttribute('aria-expanded', 'false');
          }
        });
      }

      document.addEventListener('click', (event) => {
        if (
          this.estimatorTooltipPanel &&
          this.estimatorTooltipBtn &&
          !this.root.contains(event.target)
        ) {
          this.estimatorTooltipPanel.classList.remove('is-open');
          this.estimatorTooltipBtn.setAttribute('aria-expanded', 'false');
        }
      });

      this.bindHelpTooltip('size');
      this.bindHelpTooltip('width');
    }

    bindHelpTooltip(key) {
      const helpBtn = this.root.querySelector(`[data-css-help-trigger="${key}"]`);
      const helpPanel = this.root.querySelector(`[data-css-help-panel="${key}"]`);
      if (!helpBtn || !helpPanel) return;

      helpBtn.addEventListener('click', (event) => {
        event.preventDefault();
        const willOpen = !helpPanel.classList.contains('is-open');
        helpPanel.classList.toggle('is-open', willOpen);
        helpBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });

      helpBtn.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          helpPanel.classList.remove('is-open');
          helpBtn.setAttribute('aria-expanded', 'false');
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) {
          helpPanel.classList.remove('is-open');
          helpBtn.setAttribute('aria-expanded', 'false');
        }
      });
    }

    getEstimatorTargetMajor() {
      if (!this.estimatorConfig || !this.estimatorWidthInput) return 0;
      const ppmCents = Number(this.estimatorConfig.ppmCents || 0);
      if (!ppmCents || ppmCents < 1) return 0;
      const typedWidth = parseEstimatorWidth(this.estimatorWidthInput.value);
      let width = typedWidth;
      if (width) {
        this.estimatorLastValidWidth = width;
      } else {
        width = this.estimatorLastValidWidth || parseEstimatorWidth(this.estimatorConfig.defaultWidth || 0) || 0.01;
      }
      const rawCents = ppmCents * width * 2;
      return ceilToMajorUnits(rawCents);
    }

    normalizeEstimatorInput() {
      if (!this.estimatorWidthInput) return;
      const typedWidth = parseEstimatorWidth(this.estimatorWidthInput.value);
      if (typedWidth) {
        this.estimatorLastValidWidth = typedWidth;
      }
      const normalized = this.estimatorLastValidWidth || 0.01;
      this.estimatorWidthInput.value = String(normalized);
    }

    renderEstimatorMajor(majorUnits) {
      if (!this.estimatorPrice) return;
      const formatter = this.createEstimatorFormatter();
      this.estimatorPrice.textContent = formatter.format(majorUnits);
    }

    updateEstimatorInstant() {
      if (!this.estimatorConfig || !this.estimatorPrice) return;
      const targetMajor = this.getEstimatorTargetMajor();
      this.estimatorDisplayedMajor = targetMajor;
      this.renderEstimatorMajor(targetMajor);
    }

    scheduleEstimatorUpdate() {
      if (!this.estimatorConfig || !this.estimatorPrice) return;
      if (this.estimatorDebounceTimer) {
        clearTimeout(this.estimatorDebounceTimer);
      }
      this.estimatorDebounceTimer = setTimeout(() => {
        this.estimatorDebounceTimer = null;
        const targetMajor = this.getEstimatorTargetMajor();
        const currentMajor = this.estimatorDisplayedMajor;
        const token = ++this.estimatorAnimToken;
        runAnimation(
          currentMajor,
          targetMajor,
          ESTIMATOR_ANIM_MS,
          (value) => {
            if (token !== this.estimatorAnimToken) return;
            this.renderEstimatorMajor(value);
          },
          () => {
            if (token !== this.estimatorAnimToken) return;
            this.estimatorDisplayedMajor = targetMajor;
            this.renderEstimatorMajor(targetMajor);
          }
        );
      }, ESTIMATOR_DEBOUNCE_MS);
    }

    closeEstimatorTooltip() {
      if (this.estimatorTooltipPanel) {
        this.estimatorTooltipPanel.classList.remove('is-open');
      }
      if (this.estimatorTooltipBtn) {
        this.estimatorTooltipBtn.setAttribute('aria-expanded', 'false');
      }
    }

    normalizeText(value) {
      return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    }

    isKnownHeadingKey(key) {
      return key === 'taped' || key === 'eyelet' || key === 'wave' || key === 'waterfall' || key === 'pocket';
    }

    normalizeHeadingOptionValue(value) {
      const normalized = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!normalized) return '';
      if (normalized.includes('eyelet')) return 'eyelet';
      if (normalized.includes('wave')) return 'wave';
      if (normalized.includes('waterfall')) return 'waterfall';
      if (normalized.includes('pocket')) return 'pocket';
      if (normalized === 'tape' || normalized.includes('taped') || /\btape\b/.test(normalized)) return 'taped';
      return '';
    }

    getHeadingKeyFromLabel(label) {
      if (!label) return '';
      const normalized = this.normalizeHeadingOptionValue(label);
      if (normalized) return normalized;
      return String(label).toLowerCase().replace(/\s+/g, ' ').trim();
    }

    getHeadingKeyFromSelect() {
      if (!this.headingSelect) return null;
      const label = this.headingSelect.value;
      const key = this.getHeadingKeyFromLabel(label);
      if (this.isKnownHeadingKey(key)) return key;
      return key || null;
    }

    getWidthListHeadingKey() {
      const headingKey = this.getHeadingKeyFromSelect();
      if (headingKey === 'eyelet') return 'eyelet';
      return 'taped';
    }

    getActiveWidthOptions() {
      const byHeading =
        (this.config && this.config.widthOptionsByHeading) || { taped: [], eyelet: [] };
      const listKey = this.getWidthListHeadingKey();
      const options = Array.isArray(byHeading[listKey]) ? byHeading[listKey] : [];
      if (options.length) return options.filter((opt) => String(opt || '').trim());
      const fallback = Array.isArray(byHeading.taped) ? byHeading.taped : [];
      return fallback.filter((opt) => String(opt || '').trim());
    }

    syncWidthOptionsForHeading(options = {}) {
      const { preserveSelection = true } = options;
      if (!this.widthSelect) return;

      const previousValue = preserveSelection ? this.widthSelect.value : '';
      const widthOptions = this.getActiveWidthOptions();
      if (!widthOptions.length) return;

      const defaultIndex = Math.max(
        0,
        Math.min(
          widthOptions.length - 1,
          (Number(this.config && this.config.defaultWidthOptionIndex) || 1) - 1
        )
      );

      this.widthSelect.innerHTML = '';
      widthOptions.forEach((label, index) => {
        const option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        if (preserveSelection && previousValue && label === previousValue) {
          option.selected = true;
        } else if (!preserveSelection && index === defaultIndex) {
          option.selected = true;
        }
        this.widthSelect.appendChild(option);
      });

      if (preserveSelection && previousValue && widthOptions.includes(previousValue)) {
        this.widthSelect.value = previousValue;
      } else if (!this.widthSelect.value && widthOptions[defaultIndex]) {
        this.widthSelect.value = widthOptions[defaultIndex];
      } else if (!this.widthSelect.value && widthOptions[0]) {
        this.widthSelect.value = widthOptions[0];
      }
    }

    getInlineVariantsMappingEntries() {
      const script = this.root.querySelector(`#css-variants-mapping-${this.blockId}`);
      if (!script) return [];
      try {
        const raw = JSON.parse(script.textContent);
        return this.normalizeCatalogEntries(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Curtain Size Selector: error parsing inline variants mapping', error);
        return [];
      }
    }

    normalizeCatalogEntries(rawEntries) {
      if (!Array.isArray(rawEntries)) return [];
      return rawEntries
        .map((entry) => {
          if (!entry || !entry.id) return null;
          const width =
            typeof entry.width === 'number'
              ? entry.width
              : this.parseWidthCmFromOption(entry.width);
          if (!width || width <= 0) return null;
          return {
            width,
            heading: entry.heading || null,
            id: String(entry.id),
            price: entry.price,
            available: entry.available !== false
          };
        })
        .filter(Boolean);
    }

    getMetafieldCatalogEntries() {
      const script = this.root.querySelector(`#css-metafield-catalog-${this.blockId}`);
      if (!script) return [];
      try {
        const raw = JSON.parse(script.textContent);
        return this.normalizeCatalogEntries(Array.isArray(raw) ? raw : raw.variants || []);
      } catch (error) {
        console.error('Curtain Size Selector: error parsing variant_price_catalog metafield', error);
        return [];
      }
    }

    getStorefrontApiUrl() {
      const shop = window.Shopify?.shop;
      if (!shop) return null;
      return `https://${shop}/api/2024-10/graphql.json`;
    }

    parseStorefrontVariantGid(gid) {
      if (!gid) return '';
      const match = String(gid).match(/ProductVariant\/(\d+)/);
      return match ? match[1] : '';
    }

    mapStorefrontVariantNode(node) {
      if (!node) return null;
      let width = 0;
      let heading = null;
      if (Array.isArray(node.selectedOptions)) {
        node.selectedOptions.forEach((opt) => {
          const name = String(opt.name || '').trim();
          if (/^width$/i.test(name)) {
            width = this.parseWidthCmFromOption(opt.value);
          } else if (/heading/i.test(name)) {
            heading = opt.value;
          }
        });
      }
      const id = this.parseStorefrontVariantGid(node.id);
      const amount = parseFloat(node.price?.amount);
      if (!id || !width || !Number.isFinite(amount)) return null;
      return {
        width,
        heading,
        id,
        price: Math.round(amount * 100),
        available: node.availableForSale !== false
      };
    }

    async fetchAllVariantsStorefront(handle) {
      const token = this.storefrontAccessToken;
      const apiUrl = this.getStorefrontApiUrl();
      if (!token || !apiUrl) return null;

      const query = `
        query ProductVariants($handle: String!, $cursor: String) {
          product(handle: $handle) {
            variants(first: 250, after: $cursor) {
              pageInfo { hasNextPage endCursor }
              edges {
                node {
                  id
                  availableForSale
                  price { amount }
                  selectedOptions { name value }
                }
              }
            }
          }
        }
      `;

      let cursor = null;
      const allEntries = [];
      let pageCount = 0;
      const maxPages = 12;

      try {
        while (pageCount < maxPages) {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({
              query,
              variables: { handle, cursor }
            })
          });

          if (!response.ok) {
            console.warn('Curtain Size Selector: Storefront API HTTP', response.status);
            break;
          }

          const payload = await response.json();
          if (payload.errors && payload.errors.length) {
            console.warn('Curtain Size Selector: Storefront API', payload.errors[0].message);
            break;
          }

          const connection = payload.data?.product?.variants;
          if (!connection) break;

          connection.edges.forEach((edge) => {
            const entry = this.mapStorefrontVariantNode(edge.node);
            if (entry) allEntries.push(entry);
          });

          if (!connection.pageInfo?.hasNextPage) break;
          cursor = connection.pageInfo.endCursor;
          pageCount += 1;
        }
      } catch (error) {
        console.warn('Curtain Size Selector: Storefront API fetch failed', error);
        return null;
      }

      if (allEntries.length > 0) {
        return { entries: allEntries, source: 'storefront-graphql' };
      }
      return null;
    }

    isCatalogLikelyIncomplete(entries, widthSkuCount) {
      if (!entries.length) return true;
      const maxWidth = entries.reduce((max, entry) => Math.max(max, entry.width || 0), 0);
      if (widthSkuCount > 84 && entries.length <= 250 && maxWidth < 1500) return true;
      return false;
    }

    getProductJsonUrls(handle) {
      const encoded = encodeURIComponent(handle);
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      return [...new Set([`${root}/products/${encoded}.js`, `/products/${encoded}.js`])];
    }

    async fetchProductJson(handle) {
      const urls = this.getProductJsonUrls(handle);
      let lastError = null;
      for (const url of urls) {
        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) {
            lastError = new Error(`HTTP ${response.status} for ${url}`);
            continue;
          }
          const product = await response.json();
          if (product && Array.isArray(product.variants) && product.variants.length > 0) {
            return { product, url };
          }
          lastError = new Error(`No variants in response from ${url}`);
        } catch (error) {
          lastError = error;
        }
      }
      if (lastError) {
        console.warn('Curtain Size Selector: product.js fetch failed', lastError.message);
      }
      return null;
    }

    detectOptionIndicesFromProduct(product) {
      const optionNames = Array.isArray(product.options) ? product.options : [];
      let widthIdx = this.widthOptionIndex;
      let headingIdx = this.headingOptionIndex;

      if (!widthIdx) {
        const widthOptionIndex = optionNames.findIndex((name) =>
          /^width$/i.test(String(name || '').trim())
        );
        if (widthOptionIndex >= 0) widthIdx = widthOptionIndex + 1;
      }

      if (!headingIdx) {
        const headingOptionIndex = optionNames.findIndex((name) =>
          /heading/i.test(String(name || '').trim())
        );
        if (headingOptionIndex >= 0) headingIdx = headingOptionIndex + 1;
      }

      return { widthIdx, headingIdx };
    }

    mapProductJsVariantsToEntries(product, widthIdx, headingIdx) {
      if (!product || !Array.isArray(product.variants)) return [];
      return product.variants
        .map((variant) => {
          const options = [variant.option1, variant.option2, variant.option3];
          const width = this.parseWidthCmFromOption(options[widthIdx - 1]);
          const heading = headingIdx ? options[headingIdx - 1] : null;
          return {
            width,
            heading: heading || null,
            id: String(variant.id),
            price: variant.price,
            available: variant.available !== false
          };
        })
        .filter((entry) => entry.width > 0 && entry.id);
    }

    getWidthSkuCountFromProduct(product) {
      if (!product || !Array.isArray(product.options)) return 0;
      const widthOption = product.options.find((opt) =>
        /^width$/i.test(String(opt.name || opt || '').trim())
      );
      if (widthOption && Array.isArray(widthOption.values)) {
        return widthOption.values.length;
      }
      return 0;
    }

    parseWidthCmFromOption(value) {
      if (value == null || value === '') return 0;
      const cleaned = String(value).replace(/cm/gi, '').replace(/\s+/g, '').trim();
      const parsed = parseInt(cleaned, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    buildVariantsMapFromEntries(entries) {
      if (!Array.isArray(entries) || !entries.length) return;

      const nextMap = { ...this.variantsMap };
      const nextData = { ...this.variantsData };
      const nextHeadingKeys = new Set(this.productHeadingKeys);
      let hasHeadingInEntries = false;

      entries.forEach((variant) => {
        if (!variant || !variant.width || !variant.id) return;
        if (variant.price == null || variant.price === '') return;

        let headingKey = '';
        if (variant.heading) {
          hasHeadingInEntries = true;
          headingKey = this.getHeadingKeyFromLabel(variant.heading);
          if (headingKey && this.isKnownHeadingKey(headingKey)) {
            nextHeadingKeys.add(headingKey);
          }
        }

        const useHeadingKey = Boolean(
          variant.heading && (this.hasHeadingVariants || hasHeadingInEntries)
        );
        const mapKey = useHeadingKey
          ? `${variant.width}|${headingKey}`
          : String(variant.width);

        nextMap[mapKey] = String(variant.id);
        const priceCents =
          typeof variant.price === 'number' ? variant.price : parseFloat(variant.price);
        nextData[mapKey] = {
          id: String(variant.id),
          price: priceCents,
          available: variant.available !== false
        };
      });

      if (Object.keys(nextMap).length === 0) return;

      this.variantsMap = nextMap;
      this.variantsData = nextData;
      this.productHeadingKeys = nextHeadingKeys;
      if (hasHeadingInEntries) {
        this.hasHeadingVariants = true;
      }
      this.rebuildAvailableWidthsCache();
    }

    rebuildAvailableWidthsCache() {
      const byHeading = {};
      Object.keys(this.variantsMap).forEach((mapKey) => {
        const pipeIndex = mapKey.indexOf('|');
        const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
        if (!Number.isFinite(width) || width <= 0) return;

        if (pipeIndex >= 0) {
          const headingKey = mapKey.slice(pipeIndex + 1);
          if (!byHeading[headingKey]) byHeading[headingKey] = new Set();
          byHeading[headingKey].add(width);
        } else {
          if (!byHeading._all) byHeading._all = new Set();
          byHeading._all.add(width);
        }
      });

      this.availableWidthsByHeading = {};
      Object.keys(byHeading).forEach((headingKey) => {
        this.availableWidthsByHeading[headingKey] = [...byHeading[headingKey]].sort(
          (a, b) => a - b
        );
      });
    }

    getAvailableWidthsForHeading(headingKey) {
      if (!this.availableWidthsByHeading || !Object.keys(this.availableWidthsByHeading).length) {
        this.rebuildAvailableWidthsCache();
      }
      if (headingKey && this.availableWidthsByHeading[headingKey]) {
        return this.availableWidthsByHeading[headingKey];
      }
      if (this.availableWidthsByHeading._all && this.availableWidthsByHeading._all.length) {
        return this.availableWidthsByHeading._all;
      }
      const union = new Set();
      Object.values(this.availableWidthsByHeading).forEach((widths) => {
        widths.forEach((width) => union.add(width));
      });
      return [...union].sort((a, b) => a - b);
    }

    snapToProductWidthGridCm(targetCm) {
      const clamped = Math.max(100, Math.min(2000, targetCm));
      if (clamped <= 100) return 100;
      const steps = Math.ceil((clamped - 100) / 10);
      return Math.min(2000, 100 + steps * 10);
    }

    resolveCatalogWidthCm(targetCm, headingKey) {
      const clamped = Math.max(100, Math.min(2000, targetCm));
      const widths = this.getAvailableWidthsForHeading(headingKey);
      if (widths.length) {
        const match = widths.find((width) => width >= clamped);
        if (match != null) return match;
      }
      return this.snapToProductWidthGridCm(clamped);
    }

    roundWidthToNearest50(width) {
      if (!width || width <= 0) return 0;
      return Math.ceil(width / 50) * 50;
    }

    roundWidthToNearest10(width) {
      if (!width || width <= 0) return 0;
      return Math.ceil(width / 10) * 10;
    }

    getVariantWidthForSelection(selectedWidthCm, headingKeyOverride = undefined) {
      if (!Number.isFinite(selectedWidthCm) || selectedWidthCm <= 0) return 0;

      let headingKey = null;
      if (headingKeyOverride !== undefined) {
        headingKey = headingKeyOverride;
      } else if (this.hasHeadingVariants) {
        headingKey = this.getHeadingKeyFromSelect();
      }

      let roundedCm;
      if (headingKey === 'eyelet') {
        roundedCm = this.roundWidthToNearest50(selectedWidthCm);
      } else {
        roundedCm = this.roundWidthToNearest10(selectedWidthCm);
      }
      if (roundedCm < 100) roundedCm = 100;
      if (roundedCm > 2000) return 0;

      if (headingKey === 'waterfall') return 0;

      return this.resolveCatalogWidthCm(roundedCm, headingKey) || 0;
    }

    getMaxCatalogWidthCm() {
      let max = 0;
      Object.keys(this.variantsMap).forEach((mapKey) => {
        const pipeIndex = mapKey.indexOf('|');
        const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
        if (Number.isFinite(width) && width > max) max = width;
      });
      return max;
    }

    lookupVariantMapKey(variantWidth, headingKey) {
      if (!variantWidth) return null;

      const candidates = [];
      if (headingKey) {
        candidates.push(`${variantWidth}|${headingKey}`);
      }
      if (this.headingSelect) {
        const key = this.getHeadingKeyFromLabel(this.headingSelect.value);
        if (key) candidates.push(`${variantWidth}|${key}`);
      }
      candidates.push(String(variantWidth));

      for (const key of candidates) {
        if (this.variantsMap[key]) return key;
      }

      const prefix = `${variantWidth}|`;
      return Object.keys(this.variantsMap).find((key) => key.startsWith(prefix)) || null;
    }

    loadFullVariantsMapping() {
      const handle = this.productHandle;
      if (!handle) {
        this.variantsFetchSettled = true;
        return Promise.resolve(false);
      }

      if (this.variantsLoadPromise) {
        return this.variantsLoadPromise;
      }

      this.variantsLoadPromise = (async () => {
        let entries = [];
        let source = '';
        let widthIdx = this.widthOptionIndex;
        let headingIdx = this.headingOptionIndex;
        let widthSkuCount = 0;

        const metafieldEntries = this.getMetafieldCatalogEntries();
        if (metafieldEntries.length) {
          entries = metafieldEntries;
          source = 'variant_price_catalog-metafield';
        }

        if (!entries.length || this.isCatalogLikelyIncomplete(entries, 191)) {
          const storefrontResult = await this.fetchAllVariantsStorefront(handle);
          if (storefrontResult && storefrontResult.entries.length > entries.length) {
            entries = storefrontResult.entries;
            source = storefrontResult.source;
          }
        }

        const productResult = await this.fetchProductJson(handle);
        if (productResult) {
          const { product } = productResult;
          const indices = this.detectOptionIndicesFromProduct(product);
          if (indices.widthIdx) widthIdx = indices.widthIdx;
          if (indices.headingIdx) headingIdx = indices.headingIdx;

          widthSkuCount = this.getWidthSkuCountFromProduct(product);

          if (!entries.length) {
            const jsEntries = this.mapProductJsVariantsToEntries(product, widthIdx, headingIdx);
            if (jsEntries.length) {
              entries = jsEntries;
              source = 'product.js';
            }
          }
        }

        if (!widthIdx && !entries.length) {
          return false;
        }

        this.widthOptionIndex = widthIdx;
        if (headingIdx) this.headingOptionIndex = headingIdx;

        if (!entries.length) return false;

        this.buildVariantsMapFromEntries(entries);
        const mapSize = Object.keys(this.variantsMap).length;
        const maxWidth = this.getMaxCatalogWidthCm();
        if (mapSize > 0) {
          console.info(
            `Curtain Size Selector: loaded ${mapSize} variant prices (max width ${maxWidth}cm) via ${source || 'unknown'}`
          );
        }
        if (this.isCatalogLikelyIncomplete(entries, widthSkuCount)) {
          console.warn(
            'Curtain Size Selector: only',
            entries.length,
            'variants loaded (max',
            maxWidth,
            'cm). Add a Storefront API token in this block, or populate product metafield custom.variant_price_catalog.'
          );
        }
        return mapSize > 0;
      })()
        .catch((error) => {
          console.error('Curtain Size Selector: failed to load product variants', error);
          return false;
        })
        .finally(() => {
          this.variantsFetchSettled = true;
        });

      return this.variantsLoadPromise;
    }

    variantFromMapKey(mapKey) {
      if (!mapKey || !this.variantsData[mapKey]) return null;
      const data = this.variantsData[mapKey];
      return {
        id: data.id,
        price: data.price,
        available: data.available !== false
      };
    }

    parseWidthCmFromLabel(widthLabel) {
      const text = String(widthLabel || '').replace(',', '.');
      const match = text.match(/(\d+(?:\.\d+)?)/);
      if (!match) return null;
      const num = parseFloat(match[1]);
      if (!Number.isFinite(num) || num <= 0) return null;
      if (num <= 10) return Math.round(num * 100);
      return Math.round(num);
    }

    variantTextBlob(variant) {
      const title = this.normalizeText(variant && variant.title);
      const options = Array.isArray(variant && variant.options)
        ? variant.options.map((opt) => this.normalizeText(opt)).join(' ')
        : '';
      return `${title} ${options}`.trim();
    }

    variantMatchesWidth(variant, selectedWidthCm) {
      if (!variant || !selectedWidthCm) return false;
      const blob = this.variantTextBlob(variant);
      const cmTokenCompact = `${selectedWidthCm}cm`;
      const cmTokenSpaced = `${selectedWidthCm} cm`;
      const meters = (selectedWidthCm / 100).toFixed(2);
      const metersOneDecimal = (selectedWidthCm / 100).toFixed(1);
      return (
        blob.includes(cmTokenCompact) ||
        blob.includes(cmTokenSpaced) ||
        blob.includes(`${meters}m`) ||
        blob.includes(`${meters} m`) ||
        blob.includes(`${metersOneDecimal}m`) ||
        blob.includes(`${metersOneDecimal} m`)
      );
    }

    variantMatchesHeading(variant, headingKey) {
      if (!headingKey) return true;
      const blob = this.variantTextBlob(variant);
      const headingLabel = this.headingSelect ? this.headingSelect.value : '';
      if (headingLabel && blob.includes(this.normalizeText(headingLabel))) return true;
      const aliases = {
        taped: ['taped', 'tape'],
        eyelet: ['eyelet'],
        pocket: ['pocket'],
        wave: ['wave'],
        waterfall: ['waterfall']
      };
      const terms = aliases[headingKey] || [headingKey];
      return terms.some((term) => blob.includes(term));
    }

    resolveSelectedVariantFromLegacyList(selectedWidthCm, headingKey, catalogWidthCm) {
      const variants = Array.isArray(this.config && this.config.variants) ? this.config.variants : [];
      if (!variants.length) return null;

      const widthToMatch = catalogWidthCm || selectedWidthCm;
      let matching = variants.filter((variant) => this.variantMatchesWidth(variant, widthToMatch));
      if (headingKey && this.hasHeadingVariants) {
        const withHeading = matching.filter((variant) => this.variantMatchesHeading(variant, headingKey));
        if (withHeading.length) matching = withHeading;
      }
      if (!matching.length) return null;
      const picked = matching.find((variant) => variant.available) || matching[0];
      return {
        id: picked.id,
        price: picked.price,
        available: picked.available !== false
      };
    }

    resolveSelectedVariant() {
      const selectedWidthCm = this.parseWidthCmFromLabel(this.widthSelect ? this.widthSelect.value : '');
      const headingKey = this.getHeadingKeyFromSelect();

      if (!selectedWidthCm) {
        const target = this.config && this.config.targetVariant;
        if (target && target.id) {
          return {
            id: target.id,
            price: target.price,
            available: target.available !== false
          };
        }
        return this.resolveSelectedVariantFromLegacyList(0, headingKey, 0);
      }

      const catalogWidthCm = this.hasHeadingVariants
        ? this.getVariantWidthForSelection(selectedWidthCm, headingKey)
        : selectedWidthCm;

      const mapKey = this.lookupVariantMapKey(catalogWidthCm, headingKey);
      const fromMap = this.variantFromMapKey(mapKey);
      if (fromMap) return fromMap;

      return this.resolveSelectedVariantFromLegacyList(selectedWidthCm, headingKey, catalogWidthCm);
    }

    applyVariantState() {
      const variant = this.currentVariant;
      const isAvailable = !!(variant && variant.id && variant.available);
      if (this.addBtn) this.addBtn.disabled = !isAvailable;
      if (!isAvailable) {
        this.showStatus(
          (this.config.texts && this.config.texts.missingVariant) || 'Variant unavailable.',
          'error'
        );
      } else {
        this.clearStatus();
      }
    }

    buildCurtainSizeText() {
      const sizeRaw = this.sizeSelect ? this.sizeSelect.value : '';
      const widthRaw = this.widthSelect ? this.widthSelect.value : '';
      if (sizeRaw && widthRaw) return `${widthRaw} (width) x ${sizeRaw} (length)`;
      if (sizeRaw) {
        const suffix = this.config && typeof this.config.standardSizeWidthSuffix === 'string'
          ? this.config.standardSizeWidthSuffix
          : '';
        return `${sizeRaw} ${suffix}`.trim();
      }
      return '';
    }

    blurActiveField() {
      const el = document.activeElement;
      if (!el) return;
      // Mobile Safari can keep the page zoomed while an input is focused.
      if (this.root && this.root.contains(el) && typeof el.blur === 'function') {
        el.blur();
      }
    }

    setMode(mode) {
      // Force release of iOS focus-zoom when switching panels.
      this.blurActiveField();

      const isMeasure = mode === 'measure';
      if (this.measurePanel) {
        this.measurePanel.hidden = !isMeasure;
        this.measurePanel.classList.toggle('is-hidden', !isMeasure);
        if (!isMeasure) this.closeEstimatorTooltip();
      }
      if (this.standardPanel) {
        this.standardPanel.hidden = isMeasure;
        this.standardPanel.classList.toggle('is-hidden', isMeasure);
      }
      this.clearStatus();
      this.updateStandardSummary({ animate: false });
    }

    scrollToNextSection() {
      const currentSection = this.root.closest('.shopify-section');
      if (!currentSection) return;
      const nextSection = currentSection.nextElementSibling;
      if (nextSection && nextSection.classList.contains('shopify-section')) {
        nextSection.scrollIntoView({
          behavior: this.config.scrollBehavior || 'smooth',
          block: 'start'
        });
      }
    }

    getQty() {
      const parsed = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
      return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    }

    setQty(value) {
      if (this.qtyInput) {
        this.qtyInput.value = String(value < 1 ? 1 : value);
      }
      this.updatePrice();
    }

    sanitizeQty() {
      if (!this.qtyInput) return;
      const clean = this.getQty();
      this.qtyInput.value = String(clean);
    }

    getStandardTotalCents() {
      const unitPrice = this.currentVariant && this.currentVariant.price ? Number(this.currentVariant.price) : 0;
      const qty = this.getQty();
      return unitPrice * qty;
    }

    renderStandardTotal(centsValue) {
      if (!this.totalPrice) return;
      this.totalPrice.textContent = this.currencyFormatter.format(centsValue / 100);
    }

    updatePriceInstant() {
      const total = this.getStandardTotalCents();
      this.displayedStandardTotalCents = total;
      this.renderStandardTotal(total);
    }

    updatePrice() {
      const targetTotal = this.getStandardTotalCents();
      const startTotal = this.displayedStandardTotalCents;
      const token = ++this.standardPriceAnimToken;
      runAnimation(
        startTotal,
        targetTotal,
        ESTIMATOR_ANIM_MS,
        (value) => {
          if (token !== this.standardPriceAnimToken) return;
          this.renderStandardTotal(value);
        },
        () => {
          if (token !== this.standardPriceAnimToken) return;
          this.displayedStandardTotalCents = targetTotal;
          this.renderStandardTotal(targetTotal);
        }
      );
    }

    updateStandardSummary(options = {}) {
      const { animate = true } = options;
      if (
        !this.standardSummary ||
        !this.standardPanel ||
        this.standardPanel.hidden ||
        !(this.config && this.config.showStandardSummary === true)
      ) {
        return;
      }

      const qty = this.getQty();
      const headingStyle = this.headingSelect ? this.headingSelect.value : '';
      const sizePart = this.buildCurtainSizeText();

      const parts = [];
      parts.push(`Qty ${qty}:`);
      if (headingStyle) parts.push(headingStyle + ',');
      if (sizePart) parts.push(sizePart);
      const nextText = parts.join(' ').replace(/\s+/g, ' ').trim();

      if (!animate) {
        this.standardSummary.textContent = nextText;
        this.standardSummary.classList.remove('is-updating');
        return;
      }

      this.standardSummary.classList.add('is-updating');
      clearTimeout(this._summaryTimer);
      this._summaryTimer = setTimeout(() => {
        this.standardSummary.textContent = nextText;
        this.standardSummary.classList.remove('is-updating');
      }, 160);
    }

    showStatus(message, type, options = {}) {
      if (!this.status) return;

      clearTimeout(this._statusHideTimer);

      if (!message || !type) {
        this.hideStatus();
        return;
      }

      this.status.textContent = message;
      this.status.classList.remove('is-success', 'is-error', 'is-visible');
      if (type === 'success') this.status.classList.add('is-success');
      if (type === 'error') this.status.classList.add('is-error');

      void this.status.offsetHeight;
      this.status.classList.add('is-visible');

      if (options.autoHide) {
        const delay = type === 'error' ? 5000 : 4000;
        this._statusHideTimer = setTimeout(() => this.hideStatus(), delay);
      }
    }

    hideStatus() {
      if (!this.status) return;
      clearTimeout(this._statusHideTimer);
      this.status.textContent = '';
      this.status.classList.remove('is-success', 'is-error', 'is-visible');
    }

    clearStatus() {
      this.hideStatus();
    }

    async fetchCartResource() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        console.warn('Curtain size selector: failed to fetch cart resource', error);
        return null;
      }
    }

    async notifyCartUpdated(options = {}) {
      const cart = await this.fetchCartResource();
      const itemCount = Number.isFinite(options.itemCount)
        ? options.itemCount
        : (cart && Number.isFinite(cart.item_count) ? cart.item_count : undefined);
      const detail = {
        resource: cart || null,
        sourceId: 'curtain-size-selector',
        data: {
          source: 'curtain-size-selector',
          itemCount
        }
      };

      document.dispatchEvent(new CustomEvent('cart:update', { detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    }

    async addToCart() {
      if (this.isAdding) return;

      const variant = this.currentVariant || {};
      if (!variant.id || !variant.available) {
        this.showStatus(
          (this.config.texts && this.config.texts.missingVariant) || 'Variant unavailable.',
          'error'
        );
        return;
      }

      const headingStyle = this.headingSelect ? this.headingSelect.value : '';
      const size = this.buildCurtainSizeText();
      const qty = this.getQty();

      this.isAdding = true;
      if (this.addBtn) {
        this.addBtn.disabled = true;
        this.addBtn.textContent =
          (this.config.texts && this.config.texts.adding) || 'Adding...';
      }
      this.clearStatus();

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: String(variant.id),
                quantity: qty,
                properties: {
                  'Heading Style': headingStyle,
                  'Curtain Size': size
                }
              }
            ]
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Add to cart failed');
        }

        this.showStatus(
          (this.config.texts && this.config.texts.success) || 'Added to cart.',
          'success',
          { autoHide: true }
        );
        await this.notifyCartUpdated();
      } catch (error) {
        console.error('Curtain size selector add-to-cart error:', error);
        this.showStatus(
          (this.config.texts && this.config.texts.error) || 'Could not add to cart.',
          'error',
          { autoHide: true }
        );
      } finally {
        this.isAdding = false;
        if (this.addBtn) {
          this.addBtn.disabled = !(variant.id && variant.available);
          this.addBtn.textContent = this.defaultAddLabel;
        }
      }
    }
  }

  function initBlocks() {
    const roots = document.querySelectorAll('[data-css-root]');
    roots.forEach((root) => {
      new CurtainSizeSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBlocks);
  } else {
    initBlocks();
  }
})();
